import { getDisplayTimeZone } from './businessTime';

/**
 * Tonnage (dump-load net weight) is only recorded for loads that physically go
 * to a landfill/transfer station and get weighed at the scale: roll-off,
 * front-loader, and grapple-truck pickups. Bulldog bags, services, and fees are
 * never weighed, so the order's Tonnage section doesn't apply to them.
 *
 * Detection is by product NAME today — the same fragile name-parsing heuristic
 * used for category labels (see frontend/src/portal/productLabels.ts and its
 * backend/website mirrors). Consolidating these onto a single structured
 * product category is tracked in #508; when that lands, switch this predicate
 * to read the structured category instead of matching the name.
 */
export const isTonnageApplicableProductName = (name?: string | null): boolean => {
  const lower = (name || '').toLowerCase();
  const isRollOff =
    lower.includes('roll off') || lower.includes('rolloff') || lower.includes('roll-off');
  const isFrontLoader = lower.includes('front') && lower.includes('loader');
  const isGrapple = lower.includes('grapple');
  return isRollOff || isFrontLoader || isGrapple;
};

/** True when any of the order's line names is a tonnage-applicable container. */
export const orderHasTonnageApplicableLine = (
  lines: ReadonlyArray<{ name?: string | null }> | null | undefined,
): boolean => (lines ?? []).some((l) => isTonnageApplicableProductName(l?.name));

/**
 * Per-line display labels for an order, disambiguating lines that share a
 * product name. Two "Grapple Truck Pickup" lines become "Grapple Truck Pickup
 * 1" and "…2"; a lone line keeps its bare name. Numbering is positional by the
 * order of `lines` (stable) and keyed by line id, so the same label can be
 * shown both where tonnage is captured (the Products panel) and where the
 * receipt is attached (the Send-Email dialog).
 */
export const buildLineLabelMap = (
  lines: ReadonlyArray<{ id: string; name?: string | null }> | null | undefined,
): Map<string, string> => {
  const list = lines ?? [];
  const counts = new Map<string, number>();
  for (const l of list) {
    const key = (l.name || '').trim().toLowerCase();
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  const seen = new Map<string, number>();
  const out = new Map<string, string>();
  for (const l of list) {
    const name = (l.name || 'Line').trim();
    const key = (l.name || '').trim().toLowerCase();
    if ((counts.get(key) || 0) > 1) {
      const n = (seen.get(key) || 0) + 1;
      seen.set(key, n);
      out.set(l.id, `${name} ${n}`);
    } else {
      out.set(l.id, name);
    }
  }
  return out;
};

/**
 * Short "Jun 23" date stamp for disambiguating multiple receipts on one line.
 * `iso` is the receipt attachment's created_at — a real event timestamp — so it
 * renders in the viewer's local timezone (getDisplayTimeZone from ./businessTime).
 * The shared formatBusinessDate helper isn't used here because callers append this
 * inline ("… — Jun 23") and need the compact month+day shape without the year.
 */
const formatReceiptDate = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: getDisplayTimeZone(),
  });
};

interface ReceiptLineRow {
  id: string;
  line_id?: string | null;
  line_label?: string | null;
  name?: string | null;
  created_at?: string | null;
}

/**
 * Display label per landfill-receipt attachment, keyed by attachment id.
 * Base label comes from the receipt's line (via {@link buildLineLabelMap}, so
 * same-named lines are numbered); a receipt whose line has no current match
 * falls back to its stored line_label snapshot. When a single line carries more
 * than one receipt (rare — a bin weighed twice in one rental), each gets a date
 * stamp appended so the tickets stay distinguishable without capping uploads.
 */
export const buildReceiptLabels = (
  receipts: ReadonlyArray<ReceiptLineRow>,
  lines: ReadonlyArray<{ id: string; name?: string | null }> | null | undefined,
): Map<string, string> => {
  const lineMap = buildLineLabelMap(lines);
  const perLine = new Map<string, number>();
  for (const r of receipts) {
    const lid = r.line_id || '';
    perLine.set(lid, (perLine.get(lid) || 0) + 1);
  }
  const out = new Map<string, string>();
  for (const r of receipts) {
    const lid = r.line_id || '';
    const base = (lid && lineMap.get(lid)) || r.line_label || r.name || 'Receipt';
    const stamp = (perLine.get(lid) || 0) > 1 ? formatReceiptDate(r.created_at) : '';
    out.set(r.id, stamp ? `${base} — ${stamp}` : base);
  }
  return out;
};

